import { PropsWithChildren, useEffect } from 'react'

import Modal from '../../../app/components/modal/Modal'
import useDeviceSize from '../../../app/hooks/useDeviceSize'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import useAppStore from '../../../../store/hooks/useAppStore'
import {
  preOrderNewOrderPanelIsOpenSelector,
  preOrderNewOrderPanelSelector,
} from '../../store/preOrderSelectors'
import {
  closeNewOrderPanel,
  resetNewOrderDetails,
  setNewOrderPanelCreatedGroupOrderStatus,
} from '../../store/preOrderSlice'
import GroupOrderCreatedModal from '../groupOrderCreated/GroupOrderCreatedModal'

import NewOrderPanel from './NewOrderPanel'
import styles from './NewOrderPanelSlot.module.scss'

/**
 * Where the new order panel opens: in place of the slot's content on
 * desktop, full screen over the page on mobile
 */
type NewOrderPanelSlotProps = PropsWithChildren<{
  /** Sizes the panel like the slot's content, on desktop */
  panelClassName?: string
}>

export default function NewOrderPanelSlot({
  children,
  panelClassName,
}: NewOrderPanelSlotProps) {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const { isMobile } = useDeviceSize()

  const isOpen = useAppSelector(preOrderNewOrderPanelIsOpenSelector)

  // A half-finished order doesn't follow the user to another page, and
  // leaving a group order confirmation dismisses it like closing it would
  useEffect(
    () => () => {
      const { createdGroupOrderStatus } = preOrderNewOrderPanelSelector(
        store.getState(),
      )

      dispatch(closeNewOrderPanel())

      if (createdGroupOrderStatus !== null) {
        dispatch(setNewOrderPanelCreatedGroupOrderStatus(null))
        dispatch(resetNewOrderDetails())
      }
    },
    [dispatch, store],
  )

  if (!isMobile) {
    return (
      <>
        {isOpen ? <NewOrderPanel className={panelClassName} /> : children}
        <GroupOrderCreatedModal />
      </>
    )
  }

  return (
    <>
      {children}
      <GroupOrderCreatedModal />
      <Modal
        isOpen={isOpen}
        onClose={() => dispatch(closeNewOrderPanel())}
        mobileModalMode="full-screen"
        contentClassName={styles.modalContent}
        hideClose
      >
        <NewOrderPanel />
      </Modal>
    </>
  )
}
